import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/ecommerce-hub';

const EcommerceHubLanding: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const title = 'Ecommerce Hub - AI Product Content Generator | Explanatum';
    const desc = 'AI-powered product content generation: Create compelling product descriptions, titles, category pages, and SEO-optimized copy that converts visitors into customers. Professional ecommerce content in seconds.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What types of product content can I generate?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Generate product titles, full descriptions, short descriptions, category page content, product reviews, feature bullet points, and SEO-optimized meta descriptions. All tailored to your brand voice and target audience.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I customize the tone and style?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Choose from multiple tones (professional, casual, luxury, technical) and writing styles. Set your brand voice once and apply it consistently across all product content.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does it work with my ecommerce platform?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Export content in formats compatible with Shopify, WooCommerce, Magento, BigCommerce, and any platform. Copy-paste or bulk import via CSV.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does it cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The Ecommerce Hub is included with Explanatum at £15.20/month. Generate unlimited product descriptions instead of paying copywriters £20-50 per product or using expensive tools like Jasper (£49/month).',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyMDAsMzAsMzAsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-6">
              <span className="text-3xl">🛒</span>
              <span className="text-sm font-semibold text-gray-700">AI-Powered Product Content</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              Product Descriptions That
              <span className="block bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent">
                Actually Convert
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Create compelling product descriptions, titles, and SEO-optimized copy in seconds. Professional ecommerce content without copywriters or expensive tools.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => {
                  track('cta_click', { location: 'hero', tool: 'ecommerce_hub' });
                  navigate('/marketing/ecommerce-hub');
                }}
                className="px-8 py-4 bg-gradient-to-r from-orange-600 to-pink-600 text-white font-bold rounded-xl hover:from-orange-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl text-lg"
              >
                Start Creating Content →
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById('features');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition shadow-md text-lg"
              >
                Explore Features
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">Seconds</div>
                <div className="text-sm text-gray-600">Per Description</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">Unlimited</div>
                <div className="text-sm text-gray-600">Products</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">£50+</div>
                <div className="text-sm text-gray-600">Saved Per Product</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete Ecommerce Content Suite</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to create professional product content
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {/* Product Titles */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Product Titles</h3>
              <p className="text-gray-600">Generate SEO-optimized, compelling product titles that grab attention and include key search terms.</p>
            </div>

            {/* Product Descriptions */}
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 border border-red-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Product Descriptions</h3>
              <p className="text-gray-600">Create detailed, persuasive descriptions highlighting features, benefits, and unique selling points.</p>
            </div>

            {/* Category Pages */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border border-pink-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Category Pages</h3>
              <p className="text-gray-600">Generate engaging category descriptions that improve SEO and help customers navigate your store.</p>
            </div>

            {/* Product Reviews */}
            <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-8 border border-rose-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-rose-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Product Reviews</h3>
              <p className="text-gray-600">Generate authentic-sounding product review templates and response templates for customer feedback.</p>
            </div>

            {/* Bulk Import */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition md:col-span-2 lg:col-span-4">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Bulk Import & Export</h3>
                  <p className="text-gray-600 text-lg mb-4">Upload CSV files with hundreds of products and generate content for all at once. Export in formats compatible with any ecommerce platform.</p>
                  <div className="flex gap-4 flex-wrap">
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">Shopify</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">WooCommerce</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">Magento</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">BigCommerce</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Create Content in 3 Easy Steps</h2>
            <p className="text-xl text-gray-600">From product info to conversion-optimized copy</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { step: 1, title: 'Enter Product Details', desc: 'Add product name, features, specifications, and target audience. Or upload CSV for bulk generation.', icon: '📝' },
              { step: 2, title: 'Choose Style & Tone', desc: 'Select your brand voice, content length, and focus (features, benefits, technical specs, etc.)', icon: '🎨' },
              { step: 3, title: 'Generate & Export', desc: 'AI creates optimized content instantly. Edit if needed, then export to your ecommerce platform.', icon: '🚀' },
            ].map((item) => (
              <div key={item.step} className="flex gap-6 items-start bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{item.icon}</span>
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqSchema.mainEntity.map((faq, idx) => (
              <details key={idx} className="group bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {faq.name}
                  <span className="text-orange-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 via-red-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to 10x Your Product Content?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Create unlimited product descriptions for £15.20/month. No per-product fees, no word limits.
          </p>
          <button
            onClick={() => {
              track('cta_click', { location: 'footer', tool: 'ecommerce_hub' });
              navigate('/marketing/ecommerce-hub');
            }}
            className="px-10 py-4 bg-white text-orange-600 font-bold rounded-xl hover:bg-gray-100 transition shadow-xl text-lg"
          >
            Start Creating Content →
          </button>
        </div>
      </section>
    </div>
  );
};

export default EcommerceHubLanding;
